/**
 * Backup Service - Frontend
 * 
 * ⚠️ NOTE: Backup features are Electron-only and not available in web app.
 * This service is kept for compatibility but will throw errors in browser.
 * 
 * For web app, backups should be handled server-side or through export/import.
 */

class BackupService {
  /**
   * Create manual backup
   * @param {Object} options - Backup options
   * @param {Array<string>} options.entities - Entities to backup ['all', 'invoices', 'clients', etc.]
   * @returns {Promise<Object>} Backup result
   */
  async createBackup(options = {}) {
    throw new Error('Backup features are only available in the desktop app');
    // Electron version: return await window.electron.invoke('backup:create', options);
  }

  /**
   * Download backup with save dialog
   * @param {Object} options - Backup options
   * @returns {Promise<Object>} Backup result or { canceled: true }
   */
  async downloadBackup(options = {}) {
    throw new Error('Backup features are only available in the desktop app');
    // Electron version: return await window.electron.invoke('backup:download', options);
  }

  /**
   * Get backup history for current account
   * @returns {Promise<Array>} List of backups
   */
  async getBackupHistory() {
    throw new Error('Backup features are only available in the desktop app');
    // Electron version: return await window.electron.invoke('backup:list');
  }

  /**
   * Get last backup info
   * @returns {Promise<Object|null>} Last backup or null
   */
  async getLastBackup() {
    throw new Error('Backup features are only available in the desktop app');
    // Electron version: return await window.electron.invoke('backup:getLastBackup');
  }

  /**
   * Get backup statistics
   * @returns {Promise<Object>} Backup stats
   */
  async getBackupStats() {
    throw new Error('Backup features are only available in the desktop app');
    // Electron version: return await window.electron.invoke('backup:getStats');
  }

  /**
   * Choose backup file for restore
   * @returns {Promise<Object>} { filePath: string } or { canceled: true }
   */
  async chooseBackupFile() {
    throw new Error('Backup features are only available in the desktop app');
    // Electron version: return await window.electron.invoke('backup:chooseFile');
  }

  /**
   * Get auto-backup status
   * @returns {Promise<Object>} Auto-backup status
   */
  async getAutoBackupStatus() {
    throw new Error('Backup features are only available in the desktop app');
    // Electron version: return await window.electron.invoke('backup:getAutoBackupStatus');
  }

  /**
   * Trigger auto-backup manually (for testing)
   * @returns {Promise<Object>} Result
   */
  async triggerAutoBackup() {
    throw new Error('Backup features are only available in the desktop app');
    // Electron version: return await window.electron.invoke('backup:triggerAutoBackup');
  }

  /**
   * Validate backup file
   * @param {string} filePath - Path to backup file
   * @returns {Promise<Object>} Validation result
   */
  async validateBackup(filePath) {
    throw new Error('Backup features are only available in the desktop app');
    // Electron version: return await window.electron.invoke('backup:validate', filePath);
  }

  /**
   * Preview restore
   * @param {string} filePath - Path to backup file
   * @param {Object} options - Preview options
   * @returns {Promise<Object>} Preview information
   */
  async previewRestore(filePath, options = {}) {
    throw new Error('Backup features are only available in the desktop app');
    // Electron version: return await window.electron.invoke('backup:previewRestore', filePath, options);
  }

  /**
   * Restore from backup
   * @param {string} filePath - Path to backup file
   * @param {Object} options - Restore options
   * @returns {Promise<Object>} Restore result
   */
  async restoreBackup(filePath, options = {}) {
    throw new Error('Backup features are only available in the desktop app');
    // Electron version: return await window.electron.invoke('backup:restore', filePath, options);
  }

  /**
   * Clean up old backups
   * @param {number} daysToKeep - Number of days to keep (default: 30)
   * @returns {Promise<Object>} Cleanup result
   */
  async cleanupOldBackups(daysToKeep = 30) {
    throw new Error('Backup features are only available in the desktop app');
    // Electron version: return await window.electron.invoke('backup:cleanup', daysToKeep);
  }
}

export const backupService = new BackupService();
